"use client";

import { createContext, useContext, useMemo } from "react";
import { useAgentOverlay, type UseAgentOverlayReturn } from "./useAgentOverlay";

/** The overlay publishes exactly what its hook returns. */
type AgentOverlayContextValue = UseAgentOverlayReturn;

const AgentOverlayContext = createContext<AgentOverlayContextValue | null>(null);

export function AgentOverlayProvider({ children }: { children: React.ReactNode }) {
  const overlay = useAgentOverlay();
  const {
    state,
    dispatchEvent,
    sendUserMessage,
    startConversation,
    selectScenario,
    sendConversationTurn,
    endConversation,
    requestDebrief,
    returnToTutor,
    dismiss,
    open,
  } = overlay;

  // The setters and debugRefs are stable, so the context republishes `overlay`
  // unchanged until one of these changes. Listing them is the point: the memo
  // deps are the only field list the provider still needs to spell out.
  const value = useMemo(
    () => overlay,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state,
      dispatchEvent,
      sendUserMessage,
      startConversation,
      selectScenario,
      sendConversationTurn,
      endConversation,
      requestDebrief,
      returnToTutor,
      dismiss,
      open,
    ],
  );
  return <AgentOverlayContext.Provider value={value}>{children}</AgentOverlayContext.Provider>;
}

export function useAgentOverlayContext(): AgentOverlayContextValue {
  const ctx = useContext(AgentOverlayContext);
  if (!ctx) throw new Error("useAgentOverlayContext must be used inside AgentOverlayProvider");
  return ctx;
}

/** For passive consumers (bridges) that should no-op outside the provider. */
export function useOptionalAgentOverlayContext(): AgentOverlayContextValue | null {
  return useContext(AgentOverlayContext);
}
